/****************************************************
 * Module Name:  mixStoneBucketModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Constants from "../../utility/constants";
import { Box, Button, styled, TextField, Tooltip } from "@mui/material";
import MixBucketProxy from "../../utility/proxy/manager/mixProxy/mixBucketProxy";
import logo from '../../images/pansuriya.jpg'
import Utility from "../../utility/utility";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import renderCellExpand from "../common/dataGridCellExpand";
import StoneDetailModal from "../common/stoneDetailModal";
import EditIcon from '@mui/icons-material/Edit';
import MoveDownIcon from '@mui/icons-material/MoveDown';
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CommonSnackbar from "../common/commonSnackBar";
import { LoadingButton } from "@mui/lab";
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { BackupTable } from "@mui/icons-material";
import Papa from 'papaparse';
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import PacketHistoryModal from "../common/packetHistoryModal";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import Select from "react-select";

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    '& .MuiDataGrid-columnHeader': {
        color: '#fff',
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        backgroundColor: 'grey',
    },
    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
        py: "8px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
        py: "15px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
        py: "22px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },

    "& .MuiTablePagination-selectLabel": {
        marginTop: "15px",
    },
    "& .MuiTablePagination-displayedRows": {
        marginTop: "15px",
    },
}));

// Component has major 3 different use
// 1. Reject As Mix (LabApproveReject)
// 2. Add Packet to Mix Bucket (ConvertPacket) 
// 3. Add Stone To Mix Bucket (LabNewTest, Stock, StockTally, BoxTally)
const MixStoneBucketModal = ({
    show,
    setShow,
    handleSave,
    sourceData,
    stoneId,
    loading,
    rejectComponent,
    convertPacket,
}) => {
    const { apiRef, handleCellClick } = Utility.useSingleClickEdit();
    const [remark, setRemark] = useState("");
    const [bucketOption, setBucketOption] = useState();
    const [stoneIdDetail, setStoneIdDetail] = useState("");
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
    const [mixStoneData, setMixStoneData] = useState([]);
    const [showWarning, setShowWarning] = useState(false);
    const [warningMessage, setWarningMessage] = useState("");
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [uploadLoader, setUploadLoader] = useState(false);
    const [uploadSuccessSnackbar, setUploadSuccessSnackbar] = useState(false);
    const [kapanId, setKapanId] = useState("");
    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);
    const [packetId, setPacketId] = useState("");
    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);
    const [bucketClarity, setBucketClarity] = useState(new Set());
    const [bucketShape, setBucketShape] = useState(new Set());
    const [bucketStonetype, setBucketStonetype] = useState(new Set());
    const [bucketColor, setBucketColor] = useState(new Set());
    const [bucketSize, setBucketSize] = useState(new Set());
    const stoneSelection = new Set(stoneId);

    const [openDropdown, setOpenDropdown] = useState(true);

    const statusOptions = [
        { value: "RE-LAB", label: "RE-LAB" },
        { value: "RE-CHECK", label: "RE-CHECK" },
        { value: "MIX", label: "MIX" },
    ];
    const [status, setStatus] = useState(statusOptions[0]);

    useEffect(() => {
        prepareMixStoneData();
        getAllBucketData();
    }, []);

    const prepareMixStoneData = () => {
        let mixStones = sourceData
            .filter(iStone => !rejectComponent || stoneSelection.has(iStone.stoneid))
            .map((iStone, index) => ({
                kapanid: rejectComponent ? iStone.mf_kapanid : iStone.kapanid,
                packetid: rejectComponent ? iStone.mf_packetid : iStone.packetid,
                stoneid: convertPacket ? index + 1 : iStone.stoneid,
                suratid: iStone.suratid,
                status: iStone.status,
                size: iStone.size,
                price: iStone.price,
                total: iStone.total,
                mixprice: iStone.mixprice,
                mixtotal: iStone.mixtotal
            }));
        setMixStoneData(mixStones);
    }

    const getAllBucketData = () => {
        MixBucketProxy.GetAllBucketDetailMini((response) => {
            setAllBucketData(response);
        });
    };

    const setAllBucketData = (response) => {
        let optionsArray = {}, shape = [], size = [], color = [],
            clarity = [], stonetypefullname = [];
        response.forEach((data) => {
            let key = data.stonetypefullname.toUpperCase() + " | " + data.shape.toUpperCase() + " | " +
                data.size + " | " + data.color.toUpperCase() + " | " + data.clarity.toUpperCase();
            optionsArray[key] = data.id;
            shape.push(data.shape.toUpperCase());
            size.push(data.size.toUpperCase());
            color.push(data.color.toUpperCase());
            clarity.push(data.clarity.toUpperCase());
            stonetypefullname.push(data.stonetypefullname.toUpperCase());
        });

        setBucketShape(new Set(shape));
        setBucketSize(new Set(size));
        setBucketColor(new Set(color));
        setBucketClarity(new Set(clarity));
        setBucketStonetype(new Set(stonetypefullname));
        setBucketOption(optionsArray);
    };

    const prepareStoneData = () => {
        let stoneDataArray = [];
        sourceData.forEach((iData) => {
            if (stoneSelection.has(iData.stoneid)) {
                stoneDataArray.push(iData.stoneid);
            }
        });

        return stoneDataArray;
    };

    const saveHandler = () => {

        let data;
        // Process All Mix
        if (!rejectComponent || status.value === "MIX") {
            let stoneDataArray = [];
            let lstStoneIds = [];

            let mixStones = mixStoneData.map(iMixStone => {
                let flag = true;
                // Check if no field contents falsy value
                if (!iMixStone.mixprice || !iMixStone.mixtotal || !iMixStone.bucketshape || !iMixStone.bucketsize ||
                    !iMixStone.bucketcolor || !iMixStone.bucketclarity || !iMixStone.bucketstonetype) {
                    stoneDataArray.push(iMixStone.stoneid);
                    flag = false;
                }

                if (flag) {
                    let bucket = iMixStone.bucketstonetype.toUpperCase() + " | " + iMixStone.bucketshape.toUpperCase() + " | " +
                        iMixStone.bucketsize + " | " + iMixStone.bucketcolor.toUpperCase() + " | " + iMixStone.bucketclarity.toUpperCase();
                    // Get bucketid based on the bucket name
                    if (bucketOption.hasOwnProperty(bucket)) {
                        iMixStone.mixprice = parseFloat(iMixStone.mixprice);
                        iMixStone.mixtotal = parseFloat(iMixStone.mixtotal);
                        iMixStone.bucketid = bucketOption[bucket];
                    } else {
                        // If no mapping found then add to the list and show warning
                        lstStoneIds.push(iMixStone.stoneid);
                    }
                }

                return iMixStone;
            });

            if (stoneDataArray.length > 0) {
                setShowWarning(true);
                setWarningMessage("Incomplete fields. Please insert all the value.");
            } else if (lstStoneIds.length > 0) {
                setShowWarning(true);
                setWarningMessage("Wrong bucket details for stoneids : " + lstStoneIds);
            } else {
                // Create different payload for convertPacketToMixStone
                if (convertPacket) {
                    data = mixStones.map(iMixStone => ({
                        packetid: iMixStone.packetid,
                        kapanid: iMixStone.kapanid,
                        bucketid: iMixStone.bucketid,
                        price: iMixStone.mixprice,
                        total: iMixStone.mixtotal
                    }))
                } else {
                    data = {
                        stones: mixStones,
                        userid: Constants.loggedInUser.id,
                    };
                }

                if (handleSave)
                    rejectComponent ? handleSave(status.value === "MIX", data) : handleSave(data);
            }
        } else {
            // Process Re-Lab & Re-Check
            data = {
                status: status.value,
                stones: prepareStoneData(),
                remark: remark,
            };

            if (handleSave) handleSave(status.value === "MIX", data);
        }
    };

    const processRowUpdate = (newRow, oldRow) => {
        const changedFeilds = Utility.getChangedFields(newRow, oldRow);
        if (changedFeilds.length > 0) {
            const { id, field, value } = changedFeilds[0];

            let updatedRow;
            const updatedMixStoneData = mixStoneData.map((iStone) => {
                if (iStone.stoneid === id && value) {

                    if (field === "mixprice") {
                        updatedRow = {
                            ...iStone,
                            [field]: value.toFixed(2),
                            mixtotal: (value * iStone.size).toFixed(2)
                        };
                    } else if (field === "mixtotal") {
                        updatedRow = {
                            ...iStone,
                            [field]: value.toFixed(2),
                            mixprice: (value / iStone.size).toFixed(2)
                        };
                    } else {
                        updatedRow = {
                            ...iStone,
                            [field]: value.trim(),
                        };
                    }
                    return updatedRow;
                }
                return iStone;
            })
            setMixStoneData(updatedMixStoneData);
            return updatedRow;
        } else {
            // No changes detected, return the newRow to trigger exit edit mode
            return { ...newRow };
        }
    };

    const copyPriceToMixPrice = () => {
        let updatedMixStoneData = mixStoneData.map(iStone => {
            return {
                ...iStone,
                mixprice: iStone.price.toFixed(2),
                mixtotal: iStone.total.toFixed(2)
            }
        });

        setMixStoneData(updatedMixStoneData);
    }

    const handleClear = () => {
        const fileInput = document.getElementById('file-input');
        fileInput.value = null;
    }

    const handleUpload = (file) => {
        setUploadLoader(true);
        const reader = new FileReader();
        reader.onload = () => {
            const text = reader.result;
            Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
                complete: (result) => {
                    if (result.data.length <= 0) {
                        setShowError(true);
                        setErrorMessage("No Stones Found");
                        return;
                    }

                    let headersFromCSV = Object.keys(result.data[0]);
                    let requiredColumns = ['stoneid', 'suratid', 'price', 'bucketshape',
                        'bucketsize', 'bucketcolor', 'bucketclarity', 'bucketstonetype'];
                    if (requiredColumns.length === headersFromCSV.length) {
                        let matchedList1 = [];
                        let matchedList2 = [];
                        let unmatchedList = [];
                        const stoneIdsInStockData = new Set();

                        result.data.forEach(record => {
                            mixStoneData.forEach(row => {
                                if (row.stoneid === parseInt(record['stoneid']) || row.suratid === record['suratid']) {
                                    let iRow = {
                                        ...row,
                                        mixprice: (parseFloat(record['price']).toFixed(2)),
                                        mixtotal: (row.size * parseFloat(record['price'])).toFixed(2),
                                        bucketshape: bucketShape.has(record['bucketshape'].toUpperCase() || "") ? record['bucketshape'] : "",
                                        bucketsize: bucketSize.has(record['bucketsize'].toUpperCase() || "") ? record['bucketsize'] : "",
                                        bucketcolor: bucketColor.has(record['bucketcolor'].toUpperCase() || "") ? record['bucketcolor'] : "",
                                        bucketclarity: bucketClarity.has(record['bucketclarity'].toUpperCase() || "") ? record['bucketclarity'] : "",
                                        bucketstonetype: bucketStonetype.has(record['bucketstonetype'].toUpperCase() || "") ? record['bucketstonetype'] : "",
                                        isEdited: true
                                    }

                                    if (!iRow.bucketshape || !iRow.bucketsize || !iRow.bucketcolor || !iRow.bucketclarity || !iRow.bucketstonetype) {
                                        matchedList1.push(iRow);
                                    } else {
                                        matchedList2.push(iRow);
                                    }

                                    stoneIdsInStockData.add(row.stoneid);
                                }
                                return true;
                            });
                        });

                        //to add unmatched stones down in the selection model
                        mixStoneData.forEach(iRow => {
                            if (!stoneIdsInStockData.has(iRow.stoneid)) {
                                unmatchedList.push(iRow);
                            }
                        });

                        setMixStoneData(matchedList1.concat(matchedList2).concat(unmatchedList));
                        setUploadLoader(false);
                        setUploadSuccessSnackbar(true);
                    } else {
                        console.error('CSV columns do not match table columns.');
                        setUploadLoader(false);
                        setUploadSuccessSnackbar(false);
                    }
                },
                error: (error) => {
                    console.error('Error parsing CSV:', error);
                    setShowError(true);
                }
            });
        };

        reader.readAsText(file);
    };

    const deleteMixStone = (stoneid) => {
        setMixStoneData(prevData => prevData.filter(iData => iData.stoneid !== stoneid));
    }

    let columns = [
        {
            field: "packetid",
            headerName: "PACKETID",
            headerAlign: "center",
            align: "right",
            width: 100,
            renderCell: (params) => (Utility.NeedToShowPacketDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid)
                        setPacketId(params.row.packetid)
                        setShowPacketDetailModal(true)
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid);
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            width: 70,
        },
        {
            field: "stoneid",
            headerName: "STONEID",
            width: 75,
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
                <Button
                    sx={{ textDecorationLine: "underline", color: 'black' }}
                    onClick={() => {
                        setStoneIdDetail(params.value);
                        setShowStoneDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "suratid",
            headerName: "SURATID",
            headerAlign: "center",
            align: "right",
            width: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 55,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
        },
        {
            field: "price",
            headerName: "MF PPC",
            headerAlign: "center",
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 85,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
        },
        {
            field: "total",
            headerName: "MF TOTAL",
            headerAlign: "center",
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 85,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total)
        },
        {
            field: "mixprice",
            headerName: "MIX PPC",
            headerAlign: "center",
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 90,
            editable: true,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '10px' }}>{params.row.mixprice}</span>
                    {<EditIcon style={{ marginBottom: 3, fontSize: '1rem' }} />}
                </div>
            ),
            cellClassName: (params) => 'lavenderCell',
        },
        {
            field: "mixtotal",
            headerName: "MIX TOTAL",
            headerAlign: "center",
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 90,
            editable: true,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '10px' }}>{params.row.mixtotal}</span>
                    {<EditIcon style={{ marginBottom: 3, fontSize: '1rem' }} />}
                </div>
            ),
            cellClassName: (params) => 'lavenderCell',
        },
        {
            field: "bucketshape",
            headerName: "BKT. SHAPE",
            headerAlign: "center",
            align: "right",
            width: 120,
            editable: true,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '10px' }}>{params.row.bucketshape}</span>
                    {<EditIcon style={{ marginLeft: 10, marginBottom: 3, fontSize: '1rem', }} />}
                </div>
            ),
            cellClassName: (params) => params.row.isEdited && !params.value ? 'yellowCell' : 'lavenderCell',
            renderEditCell: Utility.getDropDownComponent(Array.from(bucketShape), openDropdown, setOpenDropdown, false),
        },
        {
            field: "bucketsize",
            headerName: "BKT. SIZE",
            headerAlign: "center",
            width: 120,
            align: "right",
            editable: true,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '10px' }}>{params.row.bucketsize}</span>
                    {<EditIcon style={{ marginLeft: 10, marginBottom: 3, fontSize: '1rem', }} />}
                </div>
            ),
            cellClassName: (params) => params.row.isEdited && !params.value ? 'yellowCell' : 'lavenderCell',
            renderEditCell: Utility.getDropDownComponent(Array.from(bucketSize), openDropdown, setOpenDropdown, false),
        },
        {
            field: "bucketcolor",
            headerName: "BKT. COLOR",
            headerAlign: "center",
            align: "right",
            width: 100,
            editable: true,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '10px' }}>{params.row.bucketcolor}</span>
                    {<EditIcon style={{ marginLeft: 10, marginBottom: 3, fontSize: '1rem', }} />}
                </div>
            ),
            cellClassName: (params) => params.row.isEdited && !params.value ? 'yellowCell' : 'lavenderCell',
            renderEditCell: Utility.getDropDownComponent(Array.from(bucketColor), openDropdown, setOpenDropdown, false),
        },
        {
            field: "bucketclarity",
            headerName: "BKT. CLARITY",
            headerAlign: "center",
            align: "right",
            width: 130,
            editable: true,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '10px' }}>{params.row.bucketclarity}</span>
                    {<EditIcon style={{ marginLeft: 10, marginBottom: 3, fontSize: '1rem', }} />}
                </div>
            ),
            cellClassName: (params) => params.row.isEdited && !params.value ? 'yellowCell' : 'lavenderCell',
            renderEditCell: Utility.getDropDownComponent(Array.from(bucketClarity), openDropdown, setOpenDropdown, false),
        },
        {
            field: "bucketstonetype",
            headerName: "BKT. STONETYPE",
            headerAlign: "center",
            align: "right",
            width: 120,
            editable: true,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '10px' }}>{params.row.bucketstonetype}</span>
                    {<EditIcon style={{ marginLeft: 10, marginBottom: 3, fontSize: '1rem', }} />}
                </div>
            ),
            cellClassName: (params) => params.row.isEdited && !params.value ? 'yellowCell' : 'lavenderCell',
            renderEditCell: Utility.getDropDownComponent(Array.from(bucketStonetype), openDropdown, setOpenDropdown, false),
        },
        {
            field: "delete",
            headerName: "",
            align: "center",
            width: 50,
            renderCell: (params) => <DeleteForeverIcon sx={{ cursor: "pointer" }} color={"error"} onClick={(e) => deleteMixStone(params.row.stoneid)} />
        }
    ];

    const MixDataGrid = () => <Box
        sx={{
            marginTop: rejectComponent ? 2 : 0,
            display: "flex",
            position: "relative",
            gap: "20px",
            width: "100%",
            "& .lavenderCell": {
                color: "#080808",
                backgroundColor: "#D8BFD8",
            },
            "& .yellowCell": {
                color: "#080808",
                backgroundColor: "#F1E68C !important",
            },
            height: "400px"
        }}
    >
        <StyledDataGrid
            disableRowSelectionOnClick={true}
            density="compact"
            slots={{ toolbar: GridToolbar }}
            rows={mixStoneData}
            columns={convertPacket ? columns.filter(iCol => iCol.field !== "suratid") : columns}
            getRowId={(row) => row.stoneid}
            initialState={{
                pagination: {
                    paginationModel: Constants.pageSize,
                },
            }}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={(error) => {
                // console.error(error);
            }}
            pageSizeOptions={Constants.pageSizeOptions}
            pagination
            slotProps={{
                toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                },
            }}
            apiRef={apiRef}
            onCellClick={handleCellClick}
            editMode="cell"
        />

        <Box sx={{ position: "absolute", top: "6px", right: "265px" }}>
            <Tooltip title="Reload List">
                <RefreshOutlinedIcon
                    onClick={() => prepareMixStoneData()}
                    color="primary"
                    fontSize="large"
                    sx={{ cursor: "pointer", height: "1.6rem", width: "1.6rem" }}
                />
            </Tooltip>

            <Tooltip title="Move PPC to MIX PPC">
                <MoveDownIcon
                    onClick={() => copyPriceToMixPrice()}
                    color="success"
                    fontSize="large"
                    sx={{ cursor: "pointer", margin: "0 10px", height: "1.6rem", width: "1.6rem" }}
                />
            </Tooltip>

            <Tooltip title="Download Sample Stock Sell Upload">
                <a
                    href="/demo_excel/SampleMixBucketStoneUpload.csv"
                    download="SampleMixBucketStoneUpload.csv"
                    style={{ color: "red", marginRight: "2px", marginLeft: "2px" }}
                >
                    <BackupTable size={25} />
                </a>
            </Tooltip>

            <Tooltip title="Upload Stock Sell">
                <LoadingButton
                    color="info"
                    variant="outlined"
                    size="small"
                    sx={{ width: "105px", ml: "10px" }}
                    component="label"
                    loading={uploadLoader}
                    loadingPosition="start"
                    startIcon={<UploadFileIcon size="large" />}
                >
                    Upload
                    <input
                        id="file-input"
                        hidden
                        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                        multiple
                        type="file"
                        onChange={(e) => handleUpload(e.target.files[0])}
                        onClick={(e) => handleClear()}
                    />
                </LoadingButton>
            </Tooltip>
        </Box>
    </Box>;

    return (
        <div>

            {showStoneDetailModal ? (
                <StoneDetailModal
                    show={showStoneDetailModal}
                    stoneId={stoneIdDetail}
                    handleClose={() => setShowStoneDetailModal(false)}
                />
            ) : null}

            {showKapanDetailModal ? (
                <KapanHistoryModal
                    show={showKapanDetailModal}
                    kapanId={kapanId}
                    close={() => setShowKapanDetailModal(false)}
                />
            ) : null}

            {showPacketDetailModal ? (
                <PacketHistoryModal
                    show={showPacketDetailModal}
                    packetId={packetId}
                    kapanId={kapanId}
                    close={() => setShowPacketDetailModal(false)}
                />
            ) : null}

            <Modal
                show={show}
                onHide={() => setShow(false)}
                backdrop="static"
                keyboard={true}
                enforceFocus={false}
                dialogClassName="max-modalSize-xmd"
            >

                <CommonSnackbar
                    show={uploadSuccessSnackbar}
                    message={
                        mixStoneData.length !== 0
                            ? `File loaded Successfully`
                            : `File could not be loaded`
                    }
                    variant={mixStoneData.length !== 0 ? "success" : "error"}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={mixStoneData.length !== 0 ? "Success" : "Error"}
                    handleClose={() => setUploadSuccessSnackbar(false)}
                />

                <CommonSnackbar
                    show={showWarning}
                    message={warningMessage}
                    variant={"warning"}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={"Warning"}
                    handleClose={() => setShowWarning(false)}
                />

                <CommonSnackbar
                    show={showError}
                    message={errorMessage !== "" ? errorMessage : Constants.APiFailErrorMessage}
                    variant={"error"}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={"Error"}
                    handleClose={() => {
                        setShowError(false);
                        setErrorMessage("");
                    }}
                />

                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;{rejectComponent ? "Reject Stones" : "Mix Bucket"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {rejectComponent ? (
                        <Box display={"flex"} alignItems={"center"} gap={"35px"} sx={{ "& .form-label": { margin: 0 } }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: "15px",
                                    alignItems: "center",
                                    width: "15%",
                                }}
                            >
                                <Box>
                                    <Form.Label>Status</Form.Label>
                                </Box>
                                <Box sx={{ width: "100%" }}>
                                    <Select menuPlacement="auto"
                                        blurInputOnSelect={false}
                                        className="basic-single"
                                        classNamePrefix="select"
                                        getOptionLabel={(option) => option.label}
                                        getOptionValue={(option) => option.value}
                                        value={status}
                                        name="Status"
                                        menuPortalTarget={document.body}
                                        styles={Constants.Styles}
                                        options={statusOptions}
                                        onChange={(selectedOption) => setStatus(selectedOption)}
                                    />
                                </Box>
                            </Box>
                            {status.value !== "MIX" ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
                                    <Form.Label>Remark</Form.Label>
                                    <TextField
                                        id="outlined-multiline-static"
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                padding: "8px 14px !important"
                                            },
                                            width: "25rem",
                                        }}
                                        multiline
                                        fullWidth
                                        rows={1}
                                        placeholder="Remark"
                                        value={remark}
                                        onChange={(e) => setRemark(e.target.value)}
                                    />
                                </Box>
                            ) : null}
                        </Box>
                    ) : null}

                    {rejectComponent ? (
                        <Box>
                            {status.value === "MIX" ? <MixDataGrid /> : null}
                        </Box>
                    ) : (<MixDataGrid />)}
                </Modal.Body>
                <Modal.Footer>
                    <LoadingButton
                        variant="contained"
                        onClick={() => saveHandler()}
                        type="Submit"
                        sx={{ mr: 3 }}
                        loading={loading}
                        loadingPosition="start"
                        startIcon={<SaveIcon />}
                    >
                        Save
                    </LoadingButton>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => setShow(false)}
                        sx={{ mr: 2 }}
                        disabled={loading}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal >
        </div >
    );
}

export default MixStoneBucketModal;